import { useEffect, useRef, type ReactNode } from "react";
import type { HandleSingleSelectChange, SelectValue } from "ui-kit/src/MultipleSelect/types";
import Select from "react-select";

export interface SelectOption {
	value: number | string;
	label: string;
	type?: string;
}

interface SelectGroup {
	label: string;
	options: SelectOption[];
}

type SelectItems = SelectOption | SelectGroup;

interface SelectProps {
	items?: SelectItems[];
	placeholder?: string;
	value?: SelectValue;
	handleChange: HandleSingleSelectChange;
	isCleareable?: boolean;
	menuPortalTarget?: boolean;
	isLoading?: boolean;
	isDisabled?: boolean;
	clearValue?: boolean;
	referenced?: boolean;
	/** Optional custom renderer for option/value labels (e.g. to color-code Yes/No). Pass-through to react-select. */
	formatOptionLabel?: (option: unknown) => ReactNode;
}

export default function SelectComponent({
	value,
	placeholder,
	items,
	handleChange,
	isCleareable = true,
	menuPortalTarget,
	isLoading,
	isDisabled,
	referenced = false,
	formatOptionLabel,
}: SelectProps) {
	const customStyles = {
		//@ts-ignore
		container: (provided) => ({
			...provided,
			width: "100%",
		}),
		// @ts-ignore — must sit above admin `.modal` stack (container z-index 10120)
		menuPortal: (base) => ({ ...base, zIndex: 10200 }),
		// @ts-ignore
		menu: (provided) => ({
			...provided,
			borderRadius: "5px",
			overflow: "hidden",
			zIndex: menuPortalTarget ? 10200 : 10,
		}),
		//@ts-ignore
		control: (provided) => ({
			...provided,
			borderRadius: "5px",
			cursor: "pointer",
		}),
		//@ts-ignore
		placeholder: (provided) => ({
			...provided,
			textTransform: "uppercase",
			fontSize: "12px",
			color: "#000937",
			textWrap: "nowrap",
		}),
		//@ts-ignore
		option: (provided, state) => ({
			...provided,
			cursor: state.isDisabled ? "not-allowed" : "pointer",
			backgroundColor: state.isDisabled ? provided.backgroundColor : state.isSelected ? "#000937" : provided.backgroundColor,
			color: state.isDisabled ? provided.color : state.isSelected ? "#FFFFFF" : provided.color,
			":hover": {
				backgroundColor: state.isDisabled || state.isSelected ? provided.backgroundColor : "#000937",
				color: state.isDisabled || state.isSelected ? provided.color : "#FFFFFF",
			},
		}),
	};

	const selectRef = useRef<any>(null);
	useEffect(() => {
		if (referenced && value?.value === undefined && selectRef.current) {
			selectRef.current.clearValue();
		}
	}, [value, referenced]);

	return (
		<Select
			ref={referenced ? selectRef : undefined}
			menuPortalTarget={menuPortalTarget ? document.body : null}
			// A portalled menu is positioned from the control's viewport rectangle, which only lines up
			// with the control if the menu is placed in viewport coordinates too. Left absolute, the menu
			// is offset by the page's scroll on pages wide enough to scroll sideways.
			menuPosition={menuPortalTarget ? "fixed" : "absolute"}
			className="react-select"
			classNamePrefix="react-select"
			value={value}
			isLoading={isLoading}
			isClearable={isCleareable}
			styles={customStyles}
			onChange={handleChange}
			formatOptionLabel={formatOptionLabel}
			closeMenuOnSelect
			placeholder={placeholder}
			options={items}
			isDisabled={isDisabled}
		/>
	);
}
